import { useEffect, useState } from "react";
import moment from "moment";
import { useDownload } from "../SeparateBill/SeparateBillLayout/hooks";

import {
  TableDataRow,
  TableHeader
} from "components/organismos/GenericTable/GenericTable";
import { getCustomReportLayoutOptions } from "api/customReportLayout";
import { getStoreSalesReportData } from "api/storeSalesReport";
import { evaluateDateString } from "components/organismos/SearchPanel/SearchPanel";

// レイアウトオプションを取得するためのhook
// path を指定した場合は独立メニューに紐づく帳票を1件だけ取得する
export const useLayoutOptions = (path?: string) => {
  const [layoutOptions, setLayoutOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [searchFields, setSearchFields] = useState<any>([]);

  const fetchLayoutOptions = async () => {
    try {
      setLoading(true);
      setNotFound(false);
      const response = await getCustomReportLayoutOptions(path);

      // 独立メニューなのに帳票が紐づいていない場合は呼び出し元で404へ遷移させる
      if (path && (!response || response.length === 0)) {
        setNotFound(true);
      }

      // APIレスポンスをSelectFormのoptions形式に変換
      const options = response.map((item: any) => ({
        value: item.layoutId,
        label: item.layoutName
      }));

      setSearchFields(
        response.reduce(
          (acc: any, item: any) => ({
            ...acc,
            [item.layoutId]: item.searchConditions.map((item: any) => {
              let evaluatedValue = item.initialValue;

              // date や dateRange の場合、文字列の "new Date()" を実際のDateオブジェクトに変換
              if (
                item.type === "date" &&
                typeof item.initialValue === "string"
              ) {
                const dateValue = evaluateDateString(item.initialValue);
                if (dateValue) {
                  evaluatedValue = dateValue;
                } else {
                  console.warn(`Invalid date expression: ${item.initialValue}`);
                  evaluatedValue = null;
                }
              } else if (
                item.type === "dateRange" &&
                typeof item.initialValue === "string"
              ) {
                const dateValue = evaluateDateString(item.initialValue);
                if (dateValue) {
                  evaluatedValue = {
                    from: dateValue,
                    to: dateValue
                  };
                } else {
                  console.warn(`Invalid date expression: ${item.initialValue}`);
                  evaluatedValue = {
                    from: null,
                    to: null
                  };
                }
              }

              if (item.type === "organization") {
                evaluatedValue = {
                  orgCode: "",
                  orgName: "",
                  categoryAry: undefined
                };
              }

              return {
                ...item,
                initialValue: evaluatedValue
              };
            })
          }),
          {}
        ) || {}
      );

      // 独立メニューの場合は帳票が確定しているため、選択用のデフォルトオプションは追加しない
      setLayoutOptions(
        path
          ? options
          : [{ value: "", label: "レイアウトを選択してください" }, ...options]
      );
    } catch (error) {
      console.error("レイアウトオプションの取得に失敗しました:", error);
      if (path) {
        setNotFound(true);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLayoutOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  return {
    layoutOptions,
    loading,
    notFound,
    refetchLayoutOptions: fetchLayoutOptions,
    searchFields
  };
};

const useCustomReport = (
  layoutId: string,
  layoutName: string,
  searchFields: any[]
) => {
  const [headerRows, setHeaderRows] = useState<TableHeader>([]);
  const [dataRows, setDataRows] = useState<TableDataRow[]>([]);

  const [searchParams, setSearchParams] = useState<any>({});

  const [loading, setLoading] = useState(false);

  const dateFrom = searchParams.targetDate?.from || searchParams.targetDateFrom;
  const dateTo = searchParams.targetDate?.to || searchParams.targetDateTo;

  const dateFromFormat = (
    searchFields?.find(item => item.key === "targetDateFrom")?.additionalProps
      ?.dateFormat || "YYYY年MM月DD日"
  ).toUpperCase();

  const dateToFormat = (
    searchFields?.find(item => item.key === "targetDateTo")?.additionalProps
      ?.dateFormat || "YYYY年MM月DD日"
  ).toUpperCase();

  const {
    downloadBudgetPerfomanceManagementCSV: downloadBudgetPerfomanceManagementCSV2,
    downloadBudgetPerfomanceManagementExcel: downloadBudgetPerfomanceManagementExcel2
  } = useDownload();

  const initialReportLayout = async () => {
    if (!layoutId) {
      setHeaderRows([]);
      setDataRows([]);

      return;
    }

    setLoading(true);

    try {
      const requestParams: any = {
        layoutId: layoutId,
        loginStaffCode:
          sessionStorage.getItem("loginUser.staffCode") || "admin",
        functionType: 3, // 固定値
        monthlyComparePrevYear: 1 // 固定値
      };

      // パラメータが存在する場合のみセット
      if (searchParams.organization) {
        requestParams.orgCode =
          searchParams.organization.orgCode ||
          sessionStorage.getItem("loginUser.orgCode");

        requestParams.categoryAry =
          searchParams.organization.categoryAry || undefined;
      }

      if (searchParams.targetDate?.from) {
        requestParams.targetDateFrom = moment(
          searchParams.targetDate.from
        ).format("YYYY/MM/DD");
      }

      if (searchParams.targetDate?.to) {
        requestParams.targetDateTo = moment(searchParams.targetDate.to).format(
          "YYYY/MM/DD"
        );
      }

      if (searchParams.targetDateFrom) {
        requestParams.targetDateFrom = moment(
          searchParams.targetDateFrom
        ).format("YYYY/MM/DD");
      }

      if (!requestParams.targetDateTo) {
        requestParams.targetDateTo = "";
      }

      const response = await getStoreSalesReportData(requestParams);

      setHeaderRows(response.headers ?? []);
      setDataRows(response.data ?? []);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  // 検索条件が変更されたときに呼ばれる関数
  const handleSearchChange = (params: any) => {
    setSearchParams(params);
  };

  const handleDownloadCSV = (
    outputDataList: any[],
    headerInformation: any[][]
  ) => {
    const targetStoresData = `対象店舗：${searchParams.organization?.orgName ||
      "全ての組織"}`;

    const targetPeriodData = `対象${dateTo ? "期間" : "日付"} ：${
      dateFrom ? moment(dateFrom).format(dateFromFormat) : ""
    }${dateTo ? "~" + moment(dateTo).format(dateToFormat) : ""}`;

    const data = {
      footer: "",
      formName: layoutName,
      targetPeriod: targetPeriodData,
      targetStores: targetStoresData,
      // 前年比較対象の行を出力しない（カスタマイズ帳票は前年比較の有無が帳票ごとに異なるため。null/undefined なら出力APIが行をスキップする）
      compareToPreviousYear: undefined,
      headerInformation,
      searchResult: outputDataList,
      totalInformation: []
    };

    downloadBudgetPerfomanceManagementCSV2(data, () => {});
  };

  const handleDownloadExcel = (
    outputDataList: any[],
    headerInformation: any[][],
    mergedRegions: number[][]
  ) => {
    const targetStoresData = `対象店舗：${searchParams.organization?.orgName ||
      "全ての組織"}`;

    const targetPeriodData = `対象${dateTo ? "期間" : "日付"} ：${
      dateFrom ? moment(dateFrom).format(dateFromFormat) : ""
    }${dateTo ? "~" + moment(dateTo).format(dateToFormat) : ""}`;

    const dataExcel = {
      formName: layoutName,
      targetPeriod: targetPeriodData,
      targetStores: targetStoresData,
      // 前年比較対象の行を出力しない（カスタマイズ帳票は前年比較の有無が帳票ごとに異なるため。null/undefined なら出力APIが行をスキップする）
      compareToPreviousYear: undefined,
      headerInformation,
      searchResult: outputDataList,
      totalInformation: [],
      mergedRegions: mergedRegions,
      codeColumnIndex: []
    };

    downloadBudgetPerfomanceManagementExcel2(dataExcel, () => {});
  };

  useEffect(() => {
    if (Object.keys(searchParams).length > 0) {
      initialReportLayout();
    }
  }, [searchParams]);

  useEffect(() => {
    if (!layoutId) {
      setSearchParams({});
      return;
    }
  }, [layoutId]);

  return {
    headerRows,
    dataRows,
    handleSearchChange,
    handleDownloadCSV,
    handleDownloadExcel,
    loading
  };
};

export default useCustomReport;
